import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  Button, CircularProgress, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import API from "../API/API";

const api = new API();

// Breakfast Complimentary (same data and YES / NO marks as the main app's Complimentary Breakfast report).
// Table: Room No, Persons, Childs, Total, Breakfast (Yes / No), Room Status; VIEW shows the other details.

const BLUE = "#1976d2";
const headCell = { bgcolor: "#e3f0fc !important", color: "#0d3c6e", fontWeight: "bold", fontSize: "12px", textAlign: "center", whiteSpace: "nowrap", border: "1px solid #c9dcf0", py: 1, px: 1 };
const cell = { fontSize: "13px", textAlign: "center", border: "1px solid #e1e8f0", py: 0.8, px: 1 };

const roomPeople = (item) => (parseInt(item.persons || 0, 10) || 0) + (parseInt(item.childs || 0, 10) || 0);

const statusColor = (item) => {
  const s = (item.room_status || "").toUpperCase();
  if (item.type === "reservation") return "#0369a1";
  if (s.includes("CONTINUE")) return "#7c3aed";
  if (s.includes("OUT")) return "#dc2626";
  return "#111";
};

export default function BreakfastComplimentary() {
  const today = dayjs().format("YYYY-MM-DD");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");
  const [selectedDate, setSelectedDate] = useState(tomorrow);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [markDialog, setMarkDialog] = useState(null); // { item, mode: "yes" | "no", count }
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async (date) => {
    setLoading(true);
    try {
      const res = await api.get(`api/reports/complimentary-breakfast?date=${date}`);
      setData(res?.data || null);
    } catch (e) {
      toast.error("Failed to load breakfast data");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  const rows = Array.isArray(data?.data) ? data.data : [];
  const totals = data?.footer_totals || {};

  const openMark = (item, mode) => {
    const current = mode === "yes" ? item.ate : item.not_ate;
    setMarkDialog({ item, mode, count: item.marked && current !== null ? current : roomPeople(item) });
  };

  const saveMark = async () => {
    if (!markDialog) return;
    const people = roomPeople(markDialog.item);
    const n = parseInt(markDialog.count, 10);
    if (isNaN(n) || n < 0 || n > people) {
      toast.error(`Enter a number from 0 to ${people}`);
      return;
    }
    const ate = markDialog.mode === "yes" ? n : people - n;
    setSaving(true);
    try {
      await api.post("api/reports/complimentary-breakfast/mark", {
        date: selectedDate,
        check_in_id: markDialog.item.check_in_id,
        ate,
        not_ate: people - ate,
      }, true);
      toast.success(`Room ${markDialog.item.room_no}: ${ate} ate, ${people - ate} did not eat`);
      setMarkDialog(null);
      fetchData(selectedDate);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save breakfast");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
        <Typography sx={{ color: BLUE, fontWeight: "bold", fontSize: { xs: "20px", sm: "26px" } }}>
          Breakfast Complimentary
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {[["TODAY", today], ["TOMORROW", tomorrow]].map(([label, value]) => (
            <Button key={label} size="small" variant={selectedDate === value ? "contained" : "outlined"}
              onClick={() => setSelectedDate(value)} sx={{ fontWeight: "bold", height: 36 }}>
              {label}
            </Button>
          ))}
          {/* same 36px height as the TODAY / TOMORROW buttons */}
          <TextField type="date" size="small" value={selectedDate} onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
            sx={{ width: 160, "& .MuiInputBase-root": { height: 36 }, "& input": { textTransform: "none", py: 0 } }} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Room No", "Persons", "Childs", "Total", "Breakfast (Yes / No)", "Room Status", ""].map((h, i) => (
                  <TableCell key={i} sx={headCell}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* TOTAL first (at the top), then the rooms */}
              {rows.length > 0 && (
                <TableRow sx={{ bgcolor: "#f5f9fe" }}>
                  <TableCell sx={{ ...cell, fontWeight: "bold" }}>TOTAL</TableCell>
                  <TableCell sx={{ ...cell, fontWeight: "bold" }}>{totals.persons ?? 0}</TableCell>
                  <TableCell sx={{ ...cell, fontWeight: "bold" }}>{totals.childs ?? 0}</TableCell>
                  <TableCell sx={{ ...cell, fontWeight: "bold", bgcolor: "#ffd700" }}>{totals.total ?? 0}</TableCell>
                  <TableCell sx={{ ...cell, fontWeight: "bold" }}>
                    <span style={{ color: "#2e7d32" }}>Ate {totals.ate ?? 0}</span> / <span style={{ color: "#c62828" }}>Not {totals.not_ate ?? 0}</span>
                    {totals.not_marked > 0 && <div style={{ fontSize: "10px", fontWeight: "normal", color: "#777" }}>{totals.not_marked} room(s) not marked</div>}
                  </TableCell>
                  <TableCell sx={cell} colSpan={2} />
                </TableRow>
              )}
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ ...cell, py: 4, color: "#777" }}>No CP guests for this date</TableCell></TableRow>
              ) : rows.map((r, idx) => {
                const isRes = r.type === "reservation";
                return (
                  <TableRow key={`${r.booking_id}-${r.room_no}-${idx}`} hover>
                    <TableCell sx={{ ...cell, fontWeight: "bold" }}>{r.room_no}</TableCell>
                    <TableCell sx={cell}>{r.persons || ""}</TableCell>
                    <TableCell sx={cell}>{r.childs || ""}</TableCell>
                    <TableCell sx={cell}>{isRes ? "" : (r.total || "")}</TableCell>
                    <TableCell sx={{ ...cell, minWidth: 130 }}>
                      {isRes ? "" : (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4 }}>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Button size="small" onClick={() => openMark(r, "yes")}
                              sx={{ minWidth: 0, px: 1.4, py: 0, height: 26, fontWeight: "bold", color: "#fff", bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>YES</Button>
                            <Button size="small" onClick={() => openMark(r, "no")}
                              sx={{ minWidth: 0, px: 1.4, py: 0, height: 26, fontWeight: "bold", color: "#fff", bgcolor: "#c62828", "&:hover": { bgcolor: "#8e0000" } }}>NO</Button>
                          </Box>
                          {r.marked && (
                            <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                              <span style={{ color: "#2e7d32" }}>Ate {r.ate}</span> / <span style={{ color: "#c62828" }}>Not {r.not_ate}</span>
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cell, fontWeight: "bold", color: statusColor(r) }}>{r.room_status}</TableCell>
                    <TableCell sx={cell}>
                      <Button size="small" variant="outlined" onClick={() => setViewRow(r)} sx={{ fontWeight: "bold", minWidth: 0, px: 1.5 }}>VIEW</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* VIEW: the other details of the row */}
      <Dialog open={!!viewRow} onClose={() => setViewRow(null)} maxWidth="xs" fullWidth>
        {viewRow && (
          <>
            <DialogTitle sx={{ bgcolor: BLUE, color: "#fff", fontWeight: "bold", display: "flex", alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>Room {viewRow.room_no}</Box>
              <IconButton onClick={() => setViewRow(null)} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: "16px !important" }}>
              {[
                ["Guest Name", viewRow.guest_name],
                ["Company Name", viewRow.company_name],
                ["Booking ID", viewRow.booking_id],
                ["Check In Date", viewRow.check_in_date],
                ["Check Out Date", viewRow.check_out_date],
                ["Plan", viewRow.plan || "CP"],
                ["No. of Nights", viewRow.nights],
                ["Room Status", viewRow.room_status],
              ].map(([label, value]) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.8, borderBottom: "1px solid #eee" }}>
                  <Typography sx={{ color: "#666", fontSize: "13px" }}>{label}</Typography>
                  <Typography sx={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>{value || "-"}</Typography>
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={() => setViewRow(null)}>CLOSE</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* YES: how many ate / NO: how many did not eat */}
      <Dialog open={!!markDialog} onClose={() => !saving && setMarkDialog(null)} maxWidth="xs" fullWidth>
        {markDialog && (
          <>
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Room {markDialog.item.room_no} - Breakfast: {markDialog.mode === "yes" ? "YES" : "NO"}
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: "13px", mb: 2, color: "#555" }}>
                {markDialog.item.guest_name} · {roomPeople(markDialog.item)} guest(s)
              </Typography>
              <TextField autoFocus fullWidth size="small" type="number"
                label={markDialog.mode === "yes" ? "How many ate?" : "How many did not eat?"}
                value={markDialog.count}
                onChange={(e) => setMarkDialog((p) => ({ ...p, count: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && saveMark()}
                inputProps={{ min: 0, max: roomPeople(markDialog.item) }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMarkDialog(null)} disabled={saving}>CANCEL</Button>
              <Button variant="contained" onClick={saveMark} disabled={saving}
                sx={{ bgcolor: markDialog.mode === "yes" ? "#2e7d32" : "#c62828" }}>
                {saving ? "SAVING..." : "SAVE"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
